import type {
    Control,
    FieldErrors,
    FieldValues,
    Path,
    UseFormRegister,
} from 'react-hook-form';
import type { StackProps } from 'tamagui';

import { formatWithMask, PHONE_NUMBER_MASK } from '../../utils/maskUtils';
import type { InputProps } from '../inputs/Input';
import { PhoneNumberInput } from '../inputs/PhoneNumberInput';
import {
    ControlledFormattedInput,
    type FormatterFunction,
} from './ControlledFormattedInput';

export const createMaskFormatter = (
    mask: Array<string | RegExp>,
    storeUnmasked: boolean = true,
): FormatterFunction => {
    return (input: string) => {
        const { masked, unmasked } = formatWithMask(mask, input);
        return {
            displayValue: masked,
            storedValue: storeUnmasked ? unmasked : masked,
        };
    };
};

interface ControlledPhoneNumberInputProps<
    TFieldValues extends FieldValues,
    TContext,
> extends Omit<InputProps, 'formatValueOnChange'> {
    name: Path<TFieldValues>;
    control: Control<TFieldValues, TContext>;
    register: UseFormRegister<TFieldValues>;
    errors: FieldErrors<TFieldValues>;
    storeUnmasked?: boolean;
    label?: string;
    hint?: string;
    containerStyle?: StackProps;
    rightSideContent?: React.ReactNode;
}

export const ControlledPhoneNumberInput = <
    TFieldValues extends FieldValues,
    TContext,
>({
    storeUnmasked = true,
    ...props
}: ControlledPhoneNumberInputProps<TFieldValues, TContext>) => {
    return (
        <ControlledFormattedInput
            {...props}
            formatter={createMaskFormatter(PHONE_NUMBER_MASK, storeUnmasked)}
            InputComponent={PhoneNumberInput}
            inputComponentProps={{
                placeholder: props.placeholder ?? '(123) 456-7890',
            }}
        />
    );
};
